import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixHubV1DataMovementServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/_api/wix-data-hub-fs2',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/wix-data-hub-fs2',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/wix-data-hub-fs2',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/wix-data-hub-fs2',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/data-mover',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/wix-data-hub-fs2',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/wix-data-hub-fs2',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_data_movement-jobs';

/**
 * Submit a data movement job.
 *
 * > **Note**: By default, the job is submitted for the current site.
 */
export function submitJob(payload: object): RequestOptionsFactory<any> {
  function __submitJob({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.hub.v1.job',
      method: 'POST' as any,
      methodFqn: 'wix.hub.v1.DataMovementService.SubmitJob',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixHubV1DataMovementServiceUrl({
        protoPath: '/v1/jobs',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [{ path: 'job.startedAt' }, { path: 'job.finishedAt' }],
          },
        ]),
    };

    return metadata;
  }

  return __submitJob;
}

/**
 * Creates an upload URL for a data source file.
 *
 * Before submitting a job to import data from a file to a collection, call this method to generate an upload URL. Use the `uploadUrl` in the response to upload your source file. For example:
 *
 * `curl --request PUT --upload-file "${path_to_file}" "${uploadUrl}"`
 *
 * > **Note**: The URL is valid for 15 minutes.
 */
export function createFileUploadUrl(
  payload: object
): RequestOptionsFactory<any> {
  function __createFileUploadUrl({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.hub.v1.job',
      method: 'POST' as any,
      methodFqn: 'wix.hub.v1.DataMovementService.CreateFileUploadUrl',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixHubV1DataMovementServiceUrl({
        protoPath: '/v1/temporary-files/upload-url',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __createFileUploadUrl;
}

/**
 * Retrieves the specified data movement job.
 *
 * > **Note**: You can only retrieve jobs submitted for the current site.
 */
export function getJob(payload: object): RequestOptionsFactory<any> {
  function __getJob({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.hub.v1.job',
      method: 'GET' as any,
      methodFqn: 'wix.hub.v1.DataMovementService.GetJob',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixHubV1DataMovementServiceUrl({
        protoPath: '/v1/jobs/{jobId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [{ path: 'job.startedAt' }, { path: 'job.finishedAt' }],
          },
        ]),
    };

    return metadata;
  }

  return __getJob;
}

/**
 * Lists data movement jobs for the current site.
 *
 * The `listJobs()` method builds a query to retrieve a list of jobs for the current site, and returns a [`JobsQueryBuilder`](https://dev.wix.com/docs/sdk/backend-modules/data/movement-jobs/jobs-query-builder/ascending) object. The returned object contains the query definition, which is typically used to run the query using the [`find()`](https://dev.wix.com/docs/sdk/backend-modules/data/movement-jobs/jobs-query-builder/find) method.
 *
 * You can refine the query by chaining `JobsQueryBuilder` methods onto the query. `JobsQueryBuilder` methods enable you to sort, filter, and control the results `listJobs()` returns.
 *
 * `listJobs()` runs with these `JobsQueryBuilder` defaults, which you can override:
 *
 * - [`skipTo`: `0`](https://dev.wix.com/docs/sdk/backend-modules/data/movement-jobs/jobs-query-builder/skip-to)
 * - [`limit`: `50`](https://dev.wix.com/docs/sdk/backend-modules/data/movement-jobs/jobs-query-builder/limit)
 * - [`ascending`: by `_id`](https://dev.wix.com/docs/sdk/backend-modules/data/movement-jobs/jobs-query-builder/ascending)
 *
 * The methods chained to `listJobs()` are applied in the order they're called. For example, if you apply `ascending('firstName')` and then `descending('age')`, the results are sorted first by firstName, and then, if there are multiple results with the same `firstName`, the items are sorted by `age`.
 */
export function listJobs(payload: object): RequestOptionsFactory<any> {
  function __listJobs({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.hub.v1.job',
      method: 'GET' as any,
      methodFqn: 'wix.hub.v1.DataMovementService.ListJobs',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixHubV1DataMovementServiceUrl({
        protoPath: '/v1/jobs',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload, true),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [{ path: 'jobs.startedAt' }, { path: 'jobs.finishedAt' }],
          },
        ]),
      fallback: [
        {
          method: 'POST' as any,
          url: resolveWixHubV1DataMovementServiceUrl({
            protoPath: '/v1/jobs/list',
            data: payload,
            host,
          }),
          data: payload,
        },
      ],
    };

    return metadata;
  }

  return __listJobs;
}

/**
 * Retrieves data movement jobs for the current site, on the basis of the filtering, sorting, and paging preferences you provide.
 *
 * Learn more about [API Query Language](https://dev.wix.com/api/rest/getting-started/api-query-language) and [sorting and paging](https://dev.wix.com/docs/rest/articles/get-started/sorting-and-paging) configuration.
 */
export function queryJobs(payload: object): RequestOptionsFactory<any> {
  function __queryJobs({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.hub.v1.job',
      method: 'GET' as any,
      methodFqn: 'wix.hub.v1.DataMovementService.QueryJobs',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixHubV1DataMovementServiceUrl({
        protoPath: '/v1/jobs/query',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload, true),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [{ path: 'jobs.startedAt' }, { path: 'jobs.finishedAt' }],
          },
        ]),
      fallback: [
        {
          method: 'POST' as any,
          url: resolveWixHubV1DataMovementServiceUrl({
            protoPath: '/v1/jobs/query',
            data: payload,
            host,
          }),
          data: payload,
        },
      ],
    };

    return metadata;
  }

  return __queryJobs;
}

/** Terminates the specified job. */
export function terminateJob(payload: object): RequestOptionsFactory<any> {
  function __terminateJob({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.hub.v1.job',
      method: 'POST' as any,
      methodFqn: 'wix.hub.v1.DataMovementService.TerminateJob',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixHubV1DataMovementServiceUrl({
        protoPath: '/v1/jobs/{jobId}/terminate',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [{ path: 'job.startedAt' }, { path: 'job.finishedAt' }],
          },
        ]),
    };

    return metadata;
  }

  return __terminateJob;
}

/**
 * Lists logs for the specified job.
 *
 * Logs are generated by the destination for each item that failed to move. Some destinations also generate logs for items that moved successfully.
 */
export function listMovementLogs(payload: object): RequestOptionsFactory<any> {
  function __listMovementLogs({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.hub.v1.job',
      method: 'GET' as any,
      methodFqn: 'wix.hub.v1.DataMovementService.ListMovementLogs',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixHubV1DataMovementServiceUrl({
        protoPath: '/v1/jobs/{jobId}/logs',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __listMovementLogs;
}
